import * as React from 'react';
import Link from 'next/link';
import Router from 'next/router';
import moment from 'moment';
import { DENSITY, EASE, QUALITY } from 'constants/ponder';
import imageUrlGenerator from '../../../utils/imageUrlGenerator';
import dateOfNextSession from '../../../utils/dateOfNextSession';
import { getSessionType, SESSION_TYPES } from '../../../utils/challengeSession';
import splitSessionName from './splitSessionName';

import {
  Badge,
  Badges,
  BackgroundStyled,
  Banner,
  CloseIcon,
  Completed,
  Content,
  DashboardButton,
  Intro,
  IntroLabels,
  IntroTop,
  Logo,
  Meta,
  Modal,
  ModalWrapper,
  NoteText,
  Notes,
  Pick,
  PickImage,
  PickLabel,
  PickName,
  PickText,
  PreviewLink,
  ProgramName,
  RatingDescription,
  RatingGrid,
  RatingLabel,
  RatingTile,
  RatingValue,
  Reflection,
  Section,
  SectionTitle,
  Tag,
  Tags,
  Thumbnail,
  Title,
  Totals,
  UpNext,
  UpNextActions,
  UpNextHint,
  UpNextLabel,
  UpNextName,
  UpNextText,
} from './styles';

const RATINGS = [
  { key: 'quality', label: 'Quality', options: QUALITY },
  { key: 'ease', label: 'Ease', options: EASE },
  { key: 'density', label: 'Density', options: DENSITY },
];

const PICKS = [
  { key: 'challengeExerciseId', label: 'Challenged you most' },
  { key: 'controlExerciseId', label: 'Most progress or control' },
];

const VARIANT_COPY = {
  challenge: {
    nextSessionHint:
      "Challenges are extra work. They don't change your progress. Your scheduled session is still waiting.",
  },
  lowIntensity: {
    nextSessionHint: 'Come back at full intensity to move forward.',
  },
};

const getVariant = (completedExercises) => {
  const types = completedExercises.map((e) => getSessionType(e.sessionExercise?.session));
  if (types.includes('challenge')) return 'challenge';
  if (types.includes('lowIntensity')) return 'lowIntensity';
  return null;
};

const plural = (count, word) => `${word}${count === 1 ? '' : 's'}`;

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="2" />
    <path d="M5 9.5l2.5 2.5L13 6.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const SessionCompleted = ({ modalData, hideModal, showModal }) => {
  const {
    session,
    schedule,
    notViewedAchievements,
    ponder,
    tags = [],
    sessionDateTimeData,
    completedExercises = [],
    program,
  } = modalData;

  const [isClosing, setIsClosing] = React.useState(false);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    Router.push('/').then(() => {
      hideModal();
      if (notViewedAchievements?.length) {
        showModal('ACHIEVEMENTS');
      }
    });
  };

  const { number, title } = splitSessionName(session?.name);
  const variantType = getVariant(completedExercises);
  const variant = VARIANT_COPY[variantType];

  const exerciseCount = completedExercises.length;
  const minutes = sessionDateTimeData?.timeLength || 0;

  const ratings = RATINGS.map(({ key, label, options }) => ({
    label,
    option: options.find((option) => option.value === modalData[key]),
  })).filter(({ option }) => option);

  const picks = PICKS.map(({ key, label }) => ({
    label,
    exercise: completedExercises.find((e) => e.exercise?.id === modalData[key])?.exercise,
  })).filter(({ exercise }) => exercise);

  const note = ponder?.trim();
  const hasReflection = picks.length > 0 || tags.length > 0 || Boolean(note);

  const scheduledDays = schedule ? Object.keys(schedule).filter((day) => schedule[day]) : [];
  const nextDate = scheduledDays.length
    ? dateOfNextSession(scheduledDays, moment().format())
    : null;
  const nextSession = program?.nextSession;
  const progressHeld = Boolean(variant) && Number(nextSession?.id) === Number(session?.id);
  const upNextLabel = progressHeld ? 'Still up next' : 'Up next';
  const nextHref = nextSession
    ? `/programs/${program.program.id}/sessions/${nextSession.id}${
        program.nextTrackSessionId ? `?trackSessionId=${program.nextTrackSessionId}` : ''
      }`
    : null;

  return (
    <>
      <BackgroundStyled onClick={handleClose} />
      <ModalWrapper>
        <Modal role="dialog" aria-modal="true" aria-label="Session completed">
          <Banner image={imageUrlGenerator(session?.picture, '720x394', '400x257')}>
            <Logo />
            <CloseIcon type="button" aria-label="Close" onClick={handleClose} />
          </Banner>

          <Content>
            <Intro>
              <IntroTop>
                <Thumbnail src={imageUrlGenerator(session?.picture, '192x192', '192x192')} alt="" />
                <IntroLabels>
                  {program?.program?.name && <ProgramName>{program.program.name}</ProgramName>}
                  <Completed>
                    <CheckIcon />
                    {number ? `Completed · ${number}` : 'Completed'}
                  </Completed>
                  {variant && (
                    <Badges>
                      <Badge>{SESSION_TYPES[variantType].label}</Badge>
                    </Badges>
                  )}
                </IntroLabels>
              </IntroTop>

              {title && <Title title={session?.name}>{title}</Title>}

              <Meta>
                <span>{moment().format('dddd, MMM D')}</span>
                <Totals>
                  <strong>{exerciseCount}</strong> {plural(exerciseCount, 'exercise')} ·{' '}
                  <strong>{minutes}</strong> min
                </Totals>
              </Meta>
            </Intro>

            {(ratings.length > 0 || hasReflection) && (
              <Section aria-labelledby="session-completed-ratings">
                <SectionTitle id="session-completed-ratings">Your ratings</SectionTitle>
                {ratings.length > 0 && (
                  <RatingGrid count={ratings.length}>
                    {ratings.map(({ label, option }) => (
                      <RatingTile key={label}>
                        <RatingLabel>{label}</RatingLabel>
                        <RatingValue>{option.name}</RatingValue>
                        <RatingDescription>{option.description}</RatingDescription>
                      </RatingTile>
                    ))}
                  </RatingGrid>
                )}

                {hasReflection && (
                  <Reflection>
                    {picks.map(({ label, exercise }) => (
                      <Pick key={label}>
                        <PickImage
                          src={imageUrlGenerator(exercise.picture, '100x100', '100x100')}
                          alt=""
                        />
                        <PickText>
                          <PickLabel>{label}</PickLabel>
                          <PickName>{exercise.name}</PickName>
                        </PickText>
                      </Pick>
                    ))}
                    {(tags.length > 0 || note) && (
                      <Notes>
                        {tags.length > 0 && (
                          <Tags aria-label="Session tags">
                            {tags.map((tag) => (
                              <Tag key={tag.id}>{tag.label}</Tag>
                            ))}
                          </Tags>
                        )}
                        {note && <NoteText>{note}</NoteText>}
                      </Notes>
                    )}
                  </Reflection>
                )}
              </Section>
            )}

            {nextSession && (
              <UpNext>
                <UpNextText>
                  <UpNextLabel>
                    {nextDate ? `${upNextLabel} · ${nextDate}` : upNextLabel}
                  </UpNextLabel>
                  <UpNextName>{nextSession.name}</UpNextName>
                  {progressHeld && <UpNextHint>{variant.nextSessionHint}</UpNextHint>}
                </UpNextText>
                <UpNextActions>
                  <Link href={nextHref} passHref>
                    <PreviewLink>Preview tutorials →</PreviewLink>
                  </Link>
                  <DashboardButton type="button" onClick={handleClose} disabled={isClosing}>
                    {isClosing ? 'Loading…' : 'Back to dashboard'}
                  </DashboardButton>
                </UpNextActions>
              </UpNext>
            )}
          </Content>
        </Modal>
      </ModalWrapper>
    </>
  );
};

export default SessionCompleted;
